/**
 * Timezone Utilities
 * Handles proper timezone conversion for scheduled dates/times
 */

/**
 * Converts a local date and time to UTC ISO string while preserving the user's intended time
 * This ensures that when a user selects "2:00 PM" in their timezone, it's stored as "2:00 PM" in that timezone
 * 
 * @param dateString - Date in YYYY-MM-DD format
 * @param timeString - Time in HH:MM format (24-hour)
 * @returns ISO string in UTC that represents the local time
 */
export function createLocalDateTime(dateString: string, timeString: string): string {
  // Parse the date and time components
  const [year, month, day] = dateString.split('-').map(Number);
  const [hours, minutes] = timeString.split(':').map(Number);
  
  // Create a Date object in the user's local timezone
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
  
  // Convert to ISO string (this will be in UTC)
  return localDate.toISOString();
}

/**
 * Extracts the local time from an ISO string without timezone conversion
 * This is useful for displaying the time that was originally selected
 * 
 * @param isoString - ISO date string (e.g., "2024-03-15T14:00:00.000Z")
 * @returns Time string in HH:MM format
 */
export function extractLocalTime(isoString: string): string {
  const date = new Date(isoString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Extracts the local date from an ISO string without timezone conversion
 * 
 * @param isoString - ISO date string
 * @returns Date string in YYYY-MM-DD format
 */
export function extractLocalDate(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date/time for display in the user's timezone
 * 
 * @param isoString - ISO date string
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatLocalDateTime(
  isoString: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = new Date(isoString);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };
  return date.toLocaleString('en-US', defaultOptions);
}
